import React, { useState, useEffect, useContext } from 'react';
import { GoogleMap, Marker, Polyline, DirectionsRenderer } from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Typography, Grid, Button, Box, Switch, FormControlLabel, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { api } from "common";
import { ThemeContext } from '../components/Theme/ThemeContext';
import moment from "moment";
import NavigationIcon from '@mui/icons-material/Navigation';

export default function DriverHome() {
  const { t } = useTranslation();
  const { updateProfile, acceptTask, cancelTask, saveUserLocation, updateBooking } = api;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentTheme } = useContext(ThemeContext);

  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const taskdata = useSelector((state) => state.taskdata);
  const tasks = taskdata && taskdata.tasks ? taskdata.tasks : [];

  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const bookings = bookinglistdata && bookinglistdata.bookings ? bookinglistdata.bookings : [];
  const activeBooking = bookings.find(
    (b) =>
      b.driver === auth.profile?.uid &&
      ['ACCEPTED', 'ARRIVED', 'STARTED', 'REACHED', 'PENDING'].includes(b.status)
  );

  const [mylocation, setMylocation] = useState(null);

  // Fallback Translator Helper
  const trans = (key, fallback) => {
    const val = t(key);
    return val === key ? fallback : val;
  };

  const isOnline = auth.profile && auth.profile.uid ? auth.profile.driverActiveStatus : false;

  useEffect(() => {
    let watchId = null;
    let intervalId = null;

    if (isOnline && navigator.geolocation) {
      const handleLocationSuccess = (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setMylocation(coords);
        saveUserLocation(coords);
      };

      const handleLocationError = (error) => {
        console.error("Error getting location: ", error);
        setMylocation(prev => {
          if (!prev) {
            const fallbackCoords = { lat: 22.3072, lng: 73.1812 };
            saveUserLocation(fallbackCoords);
            return fallbackCoords;
          }
          return prev;
        });
      };

      // Get initial position immediately
      navigator.geolocation.getCurrentPosition(
        handleLocationSuccess,
        handleLocationError,
        { enableHighAccuracy: true }
      );

      // Start watching position
      watchId = navigator.geolocation.watchPosition(
        handleLocationSuccess,
        handleLocationError,
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );

      // Periodic heartbeat to keep the timestamp fresh in Firebase
      intervalId = setInterval(() => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            saveUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          (err) => {
            console.error("Heartbeat location error:", err);
            setMylocation(prev => {
              if (prev) {
                saveUserLocation(prev);
              }
              return prev;
            });
          },
          { enableHighAccuracy: true }
        );
      }, 30000); // every 30 seconds
    } else {
      // If offline, check if we need to initialize mylocation for map view purposes
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setMylocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude
            });
          },
          (error) => {
            console.log("Error getting offline location: ", error);
            setMylocation(prev => prev || { lat: 22.3072, lng: 73.1812 });
          }
        );
      }
    }

    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId);
      }
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [isOnline, saveUserLocation]);

  const handleStatusToggle = () => {
    dispatch(updateProfile({
      uid: auth.profile.uid,
      driverActiveStatus: !isOnline
    }));
  };

  const handleAccept = (task) => {
    dispatch(acceptTask(task));
    // Redirect driver directly to the booking details page so they can progress the trip
    navigate(`/bookings/bookingdetails/${task.id}`);
  };

  const handleDecline = (taskId) => {
    dispatch(cancelTask(taskId));
  };

  const [directionsResponse, setDirectionsResponse] = useState(null);

  useEffect(() => {
    if (activeBooking && mylocation && window.google) {
      let originCoords = null;
      let destCoords = null;

      if (activeBooking.status === 'ACCEPTED' && activeBooking.pickup) {
        originCoords = mylocation;
        destCoords = { lat: activeBooking.pickup.lat, lng: activeBooking.pickup.lng };
      } else if (['ARRIVED', 'STARTED', 'REACHED', 'PENDING'].includes(activeBooking.status) && activeBooking.drop) {
        originCoords = mylocation;
        destCoords = { lat: activeBooking.drop.lat, lng: activeBooking.drop.lng };
      }

      if (originCoords && destCoords) {
        const directionsService = new window.google.maps.DirectionsService();
        directionsService.route(
          {
            origin: new window.google.maps.LatLng(originCoords.lat, originCoords.lng),
            destination: new window.google.maps.LatLng(destCoords.lat, destCoords.lng),
            travelMode: window.google.maps.TravelMode.DRIVING
          },
          (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
              setDirectionsResponse(result);
            } else {
              console.error("Directions request failed: ", status);
              setDirectionsResponse(null);
            }
          }
        );
      } else {
        setDirectionsResponse(null);
      }
    } else {
      setDirectionsResponse(null);
    }
  }, [activeBooking?.status, activeBooking?.pickup?.lat, activeBooking?.pickup?.lng, activeBooking?.drop?.lat, activeBooking?.drop?.lng, mylocation?.lat, mylocation?.lng]);

  const [openOtpDialog, setOpenOtpDialog] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [otpError, setOtpError] = useState("");

  const handleArriveActive = () => {
    dispatch(updateBooking({ ...activeBooking, status: 'ARRIVED' }));
  };

  const handleStartTripActive = () => {
    if (activeBooking.otp) {
      setOtpError("");
      setOtpInput("");
      setOpenOtpDialog(true);
    } else {
      dispatch(updateBooking({ ...activeBooking, status: 'STARTED' }));
    }
  };

  const handleOtpVerifyActive = () => {
    if (otpInput === activeBooking.otp.toString()) {
      setOpenOtpDialog(false);
      dispatch(updateBooking({ ...activeBooking, status: 'STARTED' }));
    } else {
      setOtpError(t("otp_not_match") || "OTP does not match. Please try again.");
    }
  };

  const handleEndTripActive = () => {
    dispatch(updateBooking({ ...activeBooking, status: 'REACHED' }));
  };

  const handleCompletePaymentActive = () => {
    const tripCost = parseFloat(activeBooking.payableAmount || activeBooking.trip_cost || 0).toFixed(settings.decimal || 2);
    const updated = {
      ...activeBooking,
      status: 'PAID',
      customer_paid: tripCost
    };
    if (activeBooking.payment_mode === 'cash') {
      updated.cashPaymentAmount = tripCost;
      updated.cardPaymentAmount = 0;
      updated.usedWalletMoney = 0;
    } else if (activeBooking.payment_mode === 'wallet') {
      updated.cashPaymentAmount = 0;
      updated.cardPaymentAmount = 0;
      updated.usedWalletMoney = tripCost;
    } else {
      updated.cashPaymentAmount = 0;
      updated.cardPaymentAmount = tripCost;
      updated.usedWalletMoney = 0;
    }
    dispatch(updateBooking(updated));
  };

  return (
    <div className="responsive-container" style={{ minHeight: '85vh', padding: '24px', backgroundColor: 'var(--bg-main)' }}>
      <Grid container spacing={3} style={{ height: '100%' }}>
        {/* Left Side: Control Panel */}
        <Grid item xs={12} md={4}>
          <div style={{
            background: 'var(--bg-card)',
            borderRadius: '20px',
            border: 'var(--card-border)',
            boxShadow: 'var(--shadow)',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            height: '100%'
          }}>
            <div>
              <Typography variant="h5" style={{ fontWeight: 700, color: 'var(--text-main)' }}>
                {trans('driver_dashboard', 'Driver Dashboard')}
              </Typography>
              <Typography variant="body2" style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>
                {trans('driver_dashboard_subtitle', 'Manage your availability and accept ride offers.')}
              </Typography>
            </div>

            {/* Active Status Control */}
            <div style={{
              background: 'var(--bg-main)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                  {trans('active_status', 'Active Status')}
                </Typography>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  backgroundColor: isOnline ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                  color: isOnline ? '#10b981' : '#ef4444',
                  border: isOnline ? '1px solid #10b981' : '1px solid #ef4444'
                }}>
                  {isOnline ? trans('online', 'ONLINE') : trans('offline', 'OFFLINE')}
                </span>
              </div>
              <FormControlLabel
                control={
                  <Switch
                    checked={isOnline || false}
                    onChange={handleStatusToggle}
                    color="primary"
                  />
                }
                label={isOnline ? trans('go_offline', 'Toggle to go Offline') : trans('go_online', 'Toggle to go Online')}
                style={{ color: 'var(--text-secondary)', marginLeft: 0 }}
              />
            </div>

            {/* Quick Stats */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
                <Typography variant="body2" style={{ color: 'var(--text-secondary)' }}>
                  {trans('wallet_balance', 'Wallet Balance')}
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                  {settings?.symbol}{auth.profile?.walletBalance ? parseFloat(auth.profile.walletBalance).toFixed(settings?.decimal || 2) : '0.00'}
                </Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
                <Typography variant="body2" style={{ color: 'var(--text-secondary)' }}>
                  {trans('vehicle_model', 'Vehicle Model')}
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                  {auth.profile?.vehicleModel || trans('no_vehicle', 'No Vehicle')}
                </Typography>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
                <Typography variant="body2" style={{ color: 'var(--text-secondary)' }}>
                  {trans('vehicle_number', 'Vehicle Number')}
                </Typography>
                <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--text-main)' }}>
                  {auth.profile?.vehicleNumber || '-'}
                </Typography>
              </div>
            </div>
          </div>
        </Grid>

        {/* Right Side: Map & Floating Requests */}
        <Grid item xs={12} md={8} style={{ position: 'relative' }}>
          <div 
            className="driver-map-container"
            style={{
              background: 'var(--bg-card)',
              borderRadius: '20px',
              border: 'var(--card-border)',
              boxShadow: 'var(--shadow)',
              padding: '16px',
              position: 'relative'
            }}
          >
            {mylocation && mylocation.lat ? (
              <GoogleMap
                zoom={14}
                center={mylocation}
                mapContainerStyle={{ height: '100%', borderRadius: '15px' }}
                options={{
                  styles: currentTheme?.mapStyles || []
                }}
              >
                {/* Driver Location Marker */}
                <Marker
                  position={mylocation}
                  icon={{
                    path: window.google?.maps?.SymbolPath?.FORWARD_CLOSED_ARROW || 0,
                    scale: 6,
                    fillColor: '#3b82f6',
                    fillOpacity: 0.9,
                    strokeWeight: 2,
                    strokeColor: '#ffffff'
                  }}
                />

                {/* If there is an active booking, draw exact route path to pickup or dropoff */}
                {isOnline && activeBooking && (
                  <React.Fragment>
                    {/* Pickup Marker if not arrived yet */}
                    {activeBooking.status === 'ACCEPTED' && activeBooking.pickup && (
                      <Marker
                        position={{ lat: activeBooking.pickup.lat, lng: activeBooking.pickup.lng }}
                        label="P"
                      />
                    )}

                    {/* Dropoff Marker if arrived or started */}
                    {['ARRIVED', 'STARTED', 'REACHED', 'PENDING'].includes(activeBooking.status) && activeBooking.drop && (
                      <Marker
                        position={{ lat: activeBooking.drop.lat, lng: activeBooking.drop.lng }}
                        label="D"
                      />
                    )}

                    {/* Exact Road Directions Path */}
                    {directionsResponse && (
                      <DirectionsRenderer
                        directions={directionsResponse}
                        options={{
                          polylineOptions: {
                            strokeColor: activeBooking.status === 'ACCEPTED' ? '#10b981' : '#ef4444',
                            strokeWeight: 5,
                            strokeOpacity: 0.85
                          },
                          suppressMarkers: true
                        }}
                      />
                    )}
                  </React.Fragment>
                )}

                {/* If there is no active booking, draw offered tasks */}
                {isOnline && !activeBooking && tasks && tasks.length > 0 && tasks.map((task) => (
                  <React.Fragment key={task.id}>
                    {/* Pickup Marker */}
                    <Marker
                      position={{ lat: task.pickup.lat, lng: task.pickup.lng }}
                      label="P"
                    />
                    {/* Drop Marker */}
                    <Marker
                      position={{ lat: task.drop.lat, lng: task.drop.lng }}
                      label="D"
                    />
                    {/* Direct route line */}
                    <Polyline
                      path={[
                        { lat: task.pickup.lat, lng: task.pickup.lng },
                        { lat: task.drop.lat, lng: task.drop.lng }
                      ]}
                      options={{
                        strokeColor: "#E9AE31",
                        strokeOpacity: 0.8,
                        strokeWeight: 4
                      }}
                    />
                  </React.Fragment>
                ))}
              </GoogleMap>
            ) : (
              <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body2" style={{ color: 'var(--text-secondary)' }}>
                  {trans('loading_map', 'Loading Map...')}
                </Typography>
              </Box>
            )}
          </div>

          {/* Active Booking Control Card */}
          {isOnline && activeBooking && (
            <div 
              className="driver-requests-list"
              style={{
                zIndex: 10
              }}
            >
              <div 
                style={{
                  background: 'rgba(21, 21, 24, 0.95)',
                  border: '1px solid #E9AE31',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
                  backdropFilter: 'blur(10px)',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '14px',
                  marginBottom: '16px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#E9AE31', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '15px' }}>
                      <NavigationIcon style={{ transform: 'rotate(45deg)', fontSize: '18px' }} />
                      {activeBooking.status === 'ACCEPTED' ? trans('heading_driving_to_pickup', 'Driving to Pickup') :
                       activeBooking.status === 'ARRIVED' ? trans('heading_waiting_for_rider', 'Waiting for Rider') :
                       activeBooking.status === 'STARTED' ? trans('heading_driving_to_drop', 'Driving to Destination') :
                       trans('heading_payment_pending', 'Payment Pending')}
                    </Typography>
                    <Typography variant="caption" style={{ color: 'var(--text-secondary)' }}>
                      {trans('booking_id', 'Booking ID')}: {activeBooking.id}
                    </Typography>
                  </div>
                  <Typography variant="h6" style={{ fontWeight: 700, color: 'var(--text-main)' }}>
                    {settings?.symbol}{activeBooking.estimate || activeBooking.trip_cost}
                  </Typography>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <span style={{ minWidth: '8px', height: '8px', borderRadius: '50%', background: '#10b981', marginTop: '6px' }} />
                    <Typography variant="body2" style={{ color: 'var(--text-main)', fontSize: '13px' }}>
                      <strong>{trans('pickup', 'Pickup')}:</strong> {activeBooking.pickupAddress}
                    </Typography>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <span style={{ minWidth: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', marginTop: '6px' }} />
                    <Typography variant="body2" style={{ color: 'var(--text-main)', fontSize: '13px' }}>
                      <strong>{trans('drop', 'Drop')}:</strong> {activeBooking.dropAddress}
                    </Typography>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                  {activeBooking.status === 'ACCEPTED' && (
                    <Button
                      variant="contained"
                      onClick={handleArriveActive}
                      style={{
                        flexGrow: 1,
                        textTransform: 'none',
                        backgroundColor: '#10b981',
                        color: 'white',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '14px',
                        padding: '8px 12px'
                      }}
                    >
                      {trans('arrive', 'Arrive at Pickup')}
                    </Button>
                  )}
                  {activeBooking.status === 'ARRIVED' && (
                    <Button
                      variant="contained"
                      onClick={handleStartTripActive}
                      style={{
                        flexGrow: 1,
                        textTransform: 'none',
                        backgroundColor: '#E9AE31',
                        color: 'white',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '14px',
                        padding: '8px 12px'
                      }}
                    >
                      {trans('start_trip', 'Start Trip')}
                    </Button>
                  )}
                  {activeBooking.status === 'STARTED' && (
                    <Button
                      variant="contained"
                      onClick={handleEndTripActive}
                      style={{
                        flexGrow: 1,
                        textTransform: 'none',
                        backgroundColor: '#E9AE31',
                        color: 'white',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '14px',
                        padding: '8px 12px'
                      }}
                    >
                      {trans('end_trip', 'End Trip')}
                    </Button>
                  )}
                  {(activeBooking.status === 'PENDING' || activeBooking.status === 'REACHED') && (
                    <Button
                      variant="contained"
                      onClick={handleCompletePaymentActive}
                      style={{
                        flexGrow: 1,
                        textTransform: 'none',
                        backgroundColor: '#10b981',
                        color: 'white',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '14px',
                        padding: '8px 12px'
                      }}
                    >
                      {activeBooking.payment_mode === 'cash' 
                        ? (trans('complete_cash_payment', 'Complete Cash Payment'))
                        : (trans('complete_payment', 'Complete Payment'))}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Floating/Relative Ride Requests List for multiple requests */}
          {isOnline && !activeBooking && tasks && tasks.length > 0 && (
            <div 
              className="driver-requests-list"
              style={{
                zIndex: 10
              }}
            >
              {tasks.map((task) => (
                <div 
                  key={task.id}
                  style={{
                    background: 'rgba(21, 21, 24, 0.95)',
                    border: '1px solid #E9AE31',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(10px)',
                    padding: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '14px',
                    marginBottom: '16px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#E9AE31', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '15px' }}>
                        <NavigationIcon style={{ transform: 'rotate(45deg)', fontSize: '18px' }} />
                        {trans('new_booking_request', 'New Booking Request')}
                      </Typography>
                      <Typography variant="caption" style={{ color: 'var(--text-secondary)' }}>
                        {trans('booking_time', 'Time')}: {moment(task.tripdate).format('LLL')}
                      </Typography>
                    </div>
                    <Typography variant="h6" style={{ fontWeight: 700, color: 'var(--text-main)' }}>
                      {settings?.symbol}{task.estimate}
                    </Typography>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <span style={{ minWidth: '8px', height: '8px', borderRadius: '50%', background: '#10b981', marginTop: '6px' }} />
                      <Typography variant="body2" style={{ color: 'var(--text-main)', fontSize: '13px' }}>
                        <strong>{trans('pickup', 'Pickup')}:</strong> {task.pickup.add}
                      </Typography>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <span style={{ minWidth: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', marginTop: '6px' }} />
                      <Typography variant="body2" style={{ color: 'var(--text-main)', fontSize: '13px' }}>
                        <strong>{trans('drop', 'Drop')}:</strong> {task.drop.add}
                      </Typography>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
                    <Button
                      variant="contained"
                      onClick={() => handleAccept(task)}
                      style={{
                        flexGrow: 1,
                        textTransform: 'none',
                        backgroundColor: '#10b981',
                        color: 'white',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '14px',
                        padding: '6px 12px'
                      }}
                    >
                      {trans('accept_ride', 'Accept Ride')}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleDecline(task.id)}
                      style={{
                        textTransform: 'none',
                        borderColor: '#ef4444',
                        color: '#ef4444',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '14px',
                        padding: '6px 12px'
                      }}
                    >
                      {trans('decline', 'Decline')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Grid>
      </Grid>

      {/* OTP Verification Dialog */}
      <Dialog
        open={openOtpDialog}
        onClose={() => setOpenOtpDialog(false)}
        aria-labelledby="otp-dialog-title"
      >
        <DialogTitle id="otp-dialog-title" style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 700 }}>
          {trans('enter_otp', 'Enter OTP')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            {trans('otp_prompt', 'Please enter the OTP provided by the customer to start the ride.')}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            id="otp-input"
            label="OTP Code"
            type="number"
            fullWidth
            variant="outlined"
            value={otpInput}
            onChange={(e) => setOtpInput(e.target.value)}
            error={Boolean(otpError)}
            helperText={otpError}
          />
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={() => setOpenOtpDialog(false)} color="secondary" style={{ textTransform: 'none', fontWeight: 600 }}>
            {trans('cancel', 'Cancel')}
          </Button>
          <Button onClick={handleOtpVerifyActive} color="primary" variant="contained" style={{ textTransform: 'none', fontWeight: 600, backgroundColor: '#E9AE31', color: 'white' }}>
            {trans('verify', 'Verify & Start')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
